import { isKnownNoiseEvent, scrubSensitiveData } from "@monorepo/sentry-scrub";
import { init, reactErrorHandler } from "@sentry/react";
import { posthog } from "posthog-js";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { releaseInitialLoader } from "./components/custom/app-shell-skeleton";
import { ErrorBoundary } from "./components/custom/error-boundary";
import { RootLoader } from "./components/custom/root-loader";
import { ThemeProvider } from "./components/custom/theme/theme-provider";
import "./index.css";
import { env } from "@monorepo/env/dashboard";
import { installChunkReloadHandler } from "@/lib/chunk-reload";
import { applyHttpErrorMetadata, isAuthzDenial } from "@/lib/http-error";
import { captureBoundaryError } from "@/lib/sentry-boundary-handler";
import { stripStripeSecretParams } from "@/lib/stripe-url-params";
import { POSTHOG_CONFIG } from "./analytics/posthog-config";
import Providers from "./providers";

// Redirect-based methods return with the intent secret on the URL. Strip it before
// the telemetry SDKs below init, so no sink (or history/referrer) ever sees it.
const urlWithoutStripeSecrets = stripStripeSecretParams(window.location.href);
if (urlWithoutStripeSecrets) {
  window.history.replaceState(
    window.history.state,
    "",
    urlWithoutStripeSecrets
  );
}

installChunkReloadHandler();

// Sentry configuration constants
const DEFAULT_TRACE_SAMPLE_RATE = 0.01; // 1% default trace sample rate

// Initialize Sentry only in staging or production
if (env.VITE_SENTRY_DSN) {
  init({
    dsn: env.VITE_SENTRY_DSN,
    debug: env.VITE_DEBUG,
    environment: env.VITE_ARCADE_ENVIRONMENT,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE
      ? Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE)
      : DEFAULT_TRACE_SAMPLE_RATE,
    integrations: [],
    beforeSend(event, hint) {
      if (isKnownNoiseEvent(event)) {
        return null;
      }
      // The thrown value, not the serialized event: the Engine's error name
      // lives on the HttpError instance and never reaches the event payload.
      if (isAuthzDenial(hint?.originalException)) {
        return null;
      }
      applyHttpErrorMetadata(event, hint?.originalException);
      const scrubbed = scrubSensitiveData(event);
      // Auth-recovery failures intentionally carry a minimal allowlisted
      // payload. Do not add a cross-system session-replay URL back onto them.
      if (scrubbed.tags?.authRecovery) {
        return scrubbed;
      }
      const sessionUrl = posthog.get_session_replay_url({
        withTimestamp: true,
      });
      if (sessionUrl) {
        scrubbed.tags = { ...scrubbed.tags, posthog_session_url: sessionUrl };
      }
      return scrubbed;
    },
    beforeSendTransaction: scrubSensitiveData,
  });
} else if (env.VITE_DEBUG) {
  // No DSN with VITE_DEBUG=true — log Sentry events to the console so errors surface locally
  init({ debug: true });
}

/* PostHog analytics - only initialize if key is provided */
if (env.VITE_PUBLIC_POSTHOG_KEY) {
  posthog.init(env.VITE_PUBLIC_POSTHOG_KEY, POSTHOG_CONFIG);
}

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const rootElement = document.getElementById("root") as HTMLElement;
createRoot(rootElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: reactErrorHandler((_error, _errorInfo) => {
    // Error is handled by Sentry, no additional action needed
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: captureBoundaryError,
  // Callback called when React automatically recovers from errors.
  onRecoverableError: reactErrorHandler(),
}).render(
  <StrictMode>
    <ErrorBoundary onError={() => releaseInitialLoader()}>
      <ThemeProvider defaultTheme="system" storageKey="arcade-admin-theme">
        <Suspense fallback={<RootLoader />}>
          <Providers />
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
